import React, { useRef } from 'react';
import { array } from 'prop-types';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';

import { getCategoryUrl } from '../../../util/categoryLandingHelpers';

import css from './TabletChipStrip.module.css';

/**
 * Tablet L1 chip strip (prototype 550-1023): horizontally scrolling
 * top-level category chips below the topbar; the active chip is the L1
 * whose subtree holds the current page's category slug.
 */
const TabletChipStrip = props => {
  const { categories } = props;
  const location = useLocation();

  // Mouse drag-to-scroll (same pattern as the card photo carousel): mouse
  // users can't pan an overflow container natively and the scrollbar is
  // hidden. Touch keeps native panning; a real drag suppresses chip clicks.
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const onPointerDown = e => {
    if (e.pointerType !== 'mouse') return;
    const el = e.currentTarget;
    dragRef.current = { startX: e.clientX, startLeft: el.scrollLeft, dragged: false };
    // No capture yet — capturing here would retarget the click to the strip
    // and dead-end plain chip clicks
  };
  const onPointerMove = e => {
    const s = dragRef.current;
    if (!s) return;
    const dx = e.clientX - s.startX;
    if (!s.dragged && Math.abs(dx) > 4) {
      s.dragged = true;
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    if (s.dragged) {
      e.currentTarget.scrollLeft = s.startLeft - dx;
      e.preventDefault();
    }
  };
  const onPointerUp = () => {
    const s = dragRef.current;
    dragRef.current = null;
    if (s && s.dragged) suppressClickRef.current = true;
  };
  const onClickCapture = e => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  };
  const currentSlug = (location.pathname || '').split('/')[1] || '';
  const subtreeHasSlug = category => {
    if (category.url_slug === currentSlug) return true;
    return (category.subcategories || []).some(subtreeHasSlug);
  };

  if (!categories || categories.length === 0) return null;

  // "Browse all" chip dropped (Blayne 2026-08-26): it only led home; the
  // hamburger's drill-down covers full browsing and the L1 chips are here
  return (
    <div
      className={css.root}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClickCapture={onClickCapture}
    >
      {categories.map(category => {
        const isActive = !!currentSlug && subtreeHasSlug(category);
        return (
          <Link
            key={category.id}
            className={classNames(css.chip, { [css.chipActive]: isActive })}
            to={getCategoryUrl(category.id)}
          >
            {category.name}
          </Link>
        );
      })}
    </div>
  );
};

TabletChipStrip.defaultProps = {
  categories: [],
};

TabletChipStrip.propTypes = {
  categories: array,
};

export default TabletChipStrip;
